import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { BentoCard } from "@/components/BentoCard";
import { HUDStatusBadge } from "@/components/HUDStatusBadge";
import { cn } from "@/lib/utils";
import { relativeTime, syncLabel, useSyncStore } from "@/store/syncStore";
import { useInstallStore } from "@/pwa/installStore";

const EASE = [0.22, 1, 0.36, 1] as const;

const SETTINGS_ROWS = ["Account", "Notifications", "Language", "Privacy & Security", "Help"] as const;

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cad">{children}</span>
);

const Chevron = () => (
  <svg aria-hidden width="9" height="15" viewBox="0 0 9 15" fill="none" className="text-cad transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-safety">
    <path d="M1 1l6 6.5L1 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function SettingsScreen() {
  const syncState = useSyncStore((s) => s.state);
  const lastSyncedAt = useSyncStore((s) => s.lastSyncedAt);
  const setState = useSyncStore((s) => s.setState);
  const platform = useInstallStore((s) => s.platform);
  const isStandalone = useInstallStore((s) => s.isStandalone);
  const isInstalled = useInstallStore((s) => s.isInstalled);
  const canPromptNative = useInstallStore((s) => s.canPromptNative);
  const promptInstall = useInstallStore((s) => s.promptInstall);
  const openIos = useInstallStore((s) => s.openIos);
  const showInstall = !isStandalone && !isInstalled && (canPromptNative || platform === "ios");
  const [, tick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 30000);
    const online = () => setState("online");
    const offline = () => setState("offline");
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      clearInterval(id);
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }, [setState]);

  const tone = syncState === "offline" ? "default" : syncState === "online" ? "online" : "accent";

  return (
    <AppShell>
      <section data-testid="settings-screen" className="mx-auto flex min-h-screen w-full max-w-3xl flex-col gap-6 px-5 py-6 md:px-10 md:py-10">
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="flex flex-col gap-2"
        >
          <Eyebrow>Account · Preferences</Eyebrow>
          <h1 data-testid="settings-headline" className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">Settings</h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.6, ease: EASE }}
        >
          <BentoCard data-testid="settings-sync-card" className="flex items-center justify-between gap-4 p-6">
            <div className="flex flex-col gap-2">
              <Eyebrow>Sync Status</Eyebrow>
              <span data-testid="settings-last-synced" className="font-sans text-sm text-white/55">
                {syncState === "offline" ? "Waiting to reconnect" : `Last synced: ${relativeTime(lastSyncedAt)}`}
              </span>
            </div>
            <HUDStatusBadge label="SYNC" status={syncLabel(syncState)} tone={tone} />
          </BentoCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6, ease: EASE }}
        >
          <BentoCard data-testid="settings-list" className="flex flex-col">
            {showInstall && (
              <button
                type="button"
                data-testid="settings-row-install"
                onClick={() => (platform === "ios" ? openIos() : promptInstall())}
                className="group flex items-center justify-between px-6 py-5 text-left transition-colors duration-200 hover:bg-white/[0.02]"
              >
                <span className="flex items-center gap-2 font-sans text-base text-safety">
                  <Download size={16} /> Install BuildEase
                </span>
                <Chevron />
              </button>
            )}
            {SETTINGS_ROWS.map((row, i) => (
              <button
                key={row}
                type="button"
                data-testid={`settings-row-${row.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={cn(
                  "group flex items-center justify-between px-6 py-5 text-left transition-colors duration-200 hover:bg-white/[0.02]",
                  (i !== 0 || showInstall) && "border-t border-cad/20",
                )}
              >
                <span className="font-sans text-base text-white">{row}</span>
                <Chevron />
              </button>
            ))}
          </BentoCard>
        </motion.div>
      </section>
    </AppShell>
  );
}
